"use client";

import { useState, useRef, useEffect, useCallback } from "react";

interface DialogLine {
  start: number;  // 秒數（影片的絕對時間）
  end: number;
  en: string;
  zh: string;
}

interface ScenePlayerProps {
  youtube?: string;
  start?: number;
  end?: number;
  video?: string;
  subtitle?: string;
  audio?: string;
  fallbackText?: string;
  alt: string;
  caption: string;
  source?: string;
  dialogs?: DialogLine[];
}

/* ──── 關鍵字高亮 ──── */

function highlightKeyword(text: string, keyword?: string) {
  if (!keyword) return text;
  const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part) ? (
      <span key={i} className="text-yellow-400 text-lg font-extrabold">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

/* ──────────────── YouTube 播放器 ──────────────── */

function YoutubePlayer({ youtube, start, end, alt, caption, source, dialogs, keyword }: {
  youtube: string; start?: number; end?: number; alt: string;
  caption: string; source?: string; dialogs?: DialogLine[]; keyword?: string;
}) {
  const [started, setStarted] = useState(false);
  const [currentLine, setCurrentLine] = useState<DialogLine | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 載入 YouTube IFrame API
  useEffect(() => {
    if ((window as any).YT?.Player) return;
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  }, []);

  const syncSubtitle = useCallback(() => {
    if (!playerRef.current || !dialogs?.length) return;
    try {
      const t = playerRef.current.getCurrentTime() + 0.5; // 字幕提前 0.5 秒顯示
      const line = dialogs.find((d) => t >= d.start && t < d.end) || null;
      setCurrentLine(line);
    } catch { /* player not ready */ }
  }, [dialogs]);

  const initPlayer = useCallback(() => {
    const wait = () => {
      if (!(window as any).YT?.Player) {
        setTimeout(wait, 100);
        return;
      }
      playerRef.current = new (window as any).YT.Player(containerRef.current!.querySelector(".yt-slot"), {
        videoId: youtube,
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          cc_load_policy: 0,
          start: start ?? undefined,
          end: end ?? undefined,
        },
        events: {
          onStateChange: (e: any) => {
            // 播放中 → 開始同步字幕
            if (e.data === 1) {
              if (timerRef.current) clearInterval(timerRef.current);
              timerRef.current = setInterval(syncSubtitle, 100);
            } else {
              if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
              if (e.data === 0) setCurrentLine(null); // 結束
            }
          },
        },
      });
    };
    wait();
  }, [youtube, start, end, syncSubtitle]);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  function handlePlay() {
    setStarted(true);
    setTimeout(initPlayer, 0);
  }

  function handleReplay() {
    if (!playerRef.current) return;
    playerRef.current.seekTo(start ?? 0, true);
    playerRef.current.playVideo();
  }

  return (
    <section className="mb-8 overflow-hidden rounded-2xl border-2 border-purple-200 bg-purple-50">
      <div ref={containerRef} className="relative aspect-video bg-black">
        {started ? (
          <div className="yt-slot absolute inset-0 h-full w-full" />
        ) : (
          <button
            onClick={handlePlay}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-purple-900/60 to-purple-900/80 transition hover:from-purple-900/70 hover:to-purple-900/90"
          >
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="text-white drop-shadow-lg">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
            <span className="text-sm font-medium text-white/90">點擊播放影片片段</span>
          </button>
        )}
      </div>

      {/* 同步字幕列 */}
      <div className="bg-black px-4 py-4 text-center min-h-[4.5rem] flex flex-col justify-center">
        {currentLine ? (
          <>
            <p className="text-base font-bold text-white">{highlightKeyword(currentLine.en, keyword)}</p>
            <p className="mt-1 text-base text-gray-300">{currentLine.zh}</p>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            {started ? "..." : "點擊播放，字幕將同步顯示"}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 p-4 pt-3">
        {/* 重播按鈕 */}
        {started && (
          <button
            onClick={handleReplay}
            className="shrink-0 rounded-full bg-purple-100 p-2.5 text-purple-600 hover:bg-purple-200 transition"
            title="重播"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor" />
            </svg>
          </button>
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-purple-800">🎬 {caption}</p>
          {source && <p className="text-xs text-purple-500">出自：{source}</p>}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── 主元件 ──────────────── */

export default function ScenePlayer({ youtube, start, end, video, subtitle, audio, fallbackText, alt, caption, source, dialogs }: ScenePlayerProps) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  if (youtube) {
    return (
      <YoutubePlayer
        youtube={youtube} start={start} end={end}
        alt={alt} caption={caption} source={source} dialogs={dialogs}
        keyword={fallbackText}
      />
    );
  }

  // 原本的 mp4 / audio / TTS 模式
  function toggle() {
    if (playing) {
      if (video && videoRef.current) {
        videoRef.current.pause();
      } else if (audioRef.current) {
        audioRef.current.pause();
      } else if (fallbackText) {
        speechSynthesis.pause();
      }
      setPlaying(false);
      return;
    }

    if (video && videoRef.current) {
      if (videoRef.current.paused && videoRef.current.currentTime > 0) {
        videoRef.current.play();
      } else {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      setPlaying(true);
      videoRef.current.onended = () => setPlaying(false);
    } else if (audio) {
      const el = audioRef.current || new Audio(audio);
      audioRef.current = el;
      if (el.paused && el.currentTime > 0) {
        el.play();
      } else {
        el.currentTime = 0;
        el.play();
      }
      setPlaying(true);
      el.onended = () => setPlaying(false);
    } else if (fallbackText) {
      if (speechSynthesis.paused) {
        speechSynthesis.resume();
      } else {
        const utterance = new SpeechSynthesisUtterance(fallbackText);
        utterance.lang = "en-US";
        utterance.rate = 0.9;
        utterance.onend = () => setPlaying(false);
        speechSynthesis.speak(utterance);
      }
      setPlaying(true);
    }
  }

  return (
    <section className="mb-8 overflow-hidden rounded-2xl border-2 border-purple-200 bg-purple-50">
      <div className="relative">
        {video ? (
          <video
            ref={videoRef}
            src={video}
            poster={undefined}
            className="w-full"
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
          >
            {subtitle && (
              <track kind="subtitles" src={subtitle} srcLang="en" label="English" default />
            )}
          </video>
        ) : null}
      </div>

      <div className="flex items-center gap-3 p-4">
        <button
          onClick={toggle}
          className={`shrink-0 rounded-full p-2.5 transition ${playing ? "bg-purple-600 text-white animate-pulse" : "bg-purple-100 text-purple-600 hover:bg-purple-200"}`}
          title={playing ? "暫停" : "播放"}
        >
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="4" width="6" height="16" rx="1" fill="currentColor" />
              <rect x="14" y="4" width="6" height="16" rx="1" fill="currentColor" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          )}
        </button>
        <div className="min-w-0">
          <p className="text-sm font-medium text-purple-800">🎬 {caption}</p>
          {source && <p className="text-xs text-purple-500">出自：{source}</p>}
        </div>
      </div>
    </section>
  );
}
